/**
 * Mapping from Web template keys to Widget SDK widget IDs
 * These map the template keys used in TemplatesSidebar to the widget IDs in window.BidgelyWebSdk.WIDGET_TAG
 */

type WidgetTagKey = 
  | "INSIGHTS_USAGE"
  | "INSIGHTS_SIMILAR_HOMES"
  | "RATE_PLAN_COMPARISON"
  | "INSIGHTS_MONTHLY_SUMMARY"
  | "RECOMMENDATION_TOP_TIPS"
  | "RECOMMENDATION_TIPS_LIBRARY"
  | "RECOMMENDATION_TOP_PROGRAMS"
  | "BILL_PROJECTION"
  | "CARE_ENGAGEMENT_VIEW"
  | "SURVEY"
  | "HOME_PROFILE_SURVEY"
  | "HIGH_BILL_ANALYSER"
  | "HIGH_BILL_ANALYSER_V3"
  | "USER_PREFERENCES"
  | "GB_DOWNLOAD"
  | "INSIGHTS_ACTIVITY_MAP"
  | "FAQ"
  | "CHATBOT"
  | "NOTIFICATIONS"
  | "BILL_SUMMARY"
  | "BILL_SUMMARY_V2"
  | "WEATHER"
  | "CONSUMPTION_BREAKDOWN"
  | "USAGE_HISTORY"
  | "INSIGHTS_FEED"
  | "SMART_RECOMMENDATION"
  | "ENERGY_FLOW"
  | "ENVIRONMENT"
  | "SURVEY_PROMPT_BAR"
  | "PROMOTIONAL_CONTENT"
  | "FOOTER_MAIN"
  | "SAVINGS_HUB";

export const WEB_TEMPLATE_TO_WIDGET_MAP: Record<string, WidgetTagKey | WidgetTagKey[]> = {

  'DASHBOARD.HOME.BILL_ITEMIZATION': 'CONSUMPTION_BREAKDOWN',
  'DASHBOARD.HOME.YOUR_INSIGHTS': 'INSIGHTS_FEED',
  // 'DASHBOARD.HOME': 'INSIGHTS_USAGE',
  // Multi-widget template: DASHBOARD.MY_RECOMMENDATIONS renders 3 widgets
  'DASHBOARD.MY_RECOMMENDATIONS': ['RECOMMENDATION_TOP_TIPS', 'RECOMMENDATION_TOP_PROGRAMS', 'RECOMMENDATION_TIPS_LIBRARY'],
  'DASHBOARD.HOME.BILL_PROJECTION': 'BILL_PROJECTION',
  'DASHBOARD.SURVEY': 'SURVEY',
  'DASHBOARD.HOME.BILL_SUMMARY': 'BILL_SUMMARY_V2',
  'DASHBOARD.ENERGY_INSIGHTS.BILL_ANALYSIS': 'HIGH_BILL_ANALYSER_V3',
  'DASHBOARD.HOME.CARBON_WIDGET': 'ENVIRONMENT',
  // 'MULTIPREMISE': 'INSIGHTS_USAGE',
  'DASHBOARD.HOME.FOOTER': 'FOOTER_MAIN',
  'DASHBOARD.FAQS': 'FAQ',
  'FEEDBACK': 'CARE_ENGAGEMENT_VIEW',
  'DASHBOARD.ENERGY_INSIGHTS.USAGE': 'INSIGHTS_USAGE',
  'DASHBOARD.ENERGY_INSIGHTS.MONTHLY_SUMMARY': 'INSIGHTS_MONTHLY_SUMMARY',
  'DASHBOARD.ENERGY_INSIGHTS.SHC': 'INSIGHTS_SIMILAR_HOMES',
  'DASHBOARD.HOME.BILL_HISTORY': 'USAGE_HISTORY',
  'DASHBOARD.ENERGY_INSIGHTS.RATE_PLAN': 'RATE_PLAN_COMPARISON',
  'DASHBOARD.SETTINGS_AND_PREFERENCES.ALERT_TYPE': 'USER_PREFERENCES',
  'DASHBOARD.SETTINGS_AND_PREFERENCES.CONTACT_INFO': 'USER_PREFERENCES',
  'DASHBOARD.GB_DOWNLOAD': 'GB_DOWNLOAD',
  'ENGAGEMENT_VIEW': 'CARE_ENGAGEMENT_VIEW',
  'DASHBOARD.ENERGY_INSIGHTS.ACTIVITY_MAP': 'INSIGHTS_ACTIVITY_MAP',
  'DASHBOARD.NOTIFICATIONS': 'NOTIFICATIONS',
  'DASHBOARD.HOME.PROGRAM_WIDGET': 'SMART_RECOMMENDATION',
  'DASHBOARD.HOME.SOLAR_ENERGY_FLOW': 'ENERGY_FLOW'
};

/**
 * Check if a template key is a Web widget template
 */
export const isWebWidgetTemplate = (templateKey: string | null): boolean => {
  if (!templateKey) return false;
  // Check if it's in the mapping, starts with WEB_ or DASHBOARD., or is a mock template
  return templateKey in WEB_TEMPLATE_TO_WIDGET_MAP || 
         templateKey.startsWith('WEB_') || 
         templateKey.startsWith('DASHBOARD.');
};

/**
 * Get widget ID(s) from template key
 * Handles template keys with fuel type suffix (e.g., "DASHBOARD.HOME.BILL_PROJECTION__ELECTRIC")
 * Returns a single WidgetTagKey for single-widget templates, or an array for multi-widget templates
 */
export const getWidgetIdFromTemplate = (templateKey: string | null): WidgetTagKey | WidgetTagKey[] | null => {
  if (!templateKey) return null;

  // Remove fuel type suffix if present (e.g., "__ELECTRIC", "__GAS", "__WATER")
  const baseTemplateKey = templateKey.split('__')[0];

  return WEB_TEMPLATE_TO_WIDGET_MAP[baseTemplateKey] || null;
};

/**
 * Check if a template key maps to multiple widgets
 */
export const isMultiWidgetTemplate = (templateKey: string | null): boolean => {
  if (!templateKey) return false;
  const baseTemplateKey = templateKey.split('__')[0];
  const widgetIds = WEB_TEMPLATE_TO_WIDGET_MAP[baseTemplateKey];
  return Array.isArray(widgetIds);
};

/**
 * Human-readable display name for a Web widget template key (e.g. "DASHBOARD.HOME.BILL_PROJECTION" → "Bill Projection").
 * Used in loader and placeholder text.
 */
export function getWebWidgetDisplayName(templateKey: string | null): string {
  if (!templateKey || !templateKey.trim()) return 'Web Widget';
  const parts = templateKey.trim().split('.');
  const lastPart = parts[parts.length - 1] || templateKey;
  return lastPart
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}


export const DEFAULT_WEB_WIDGET_DESKTOP_WIDTH = 600;

export const WIDE_WEB_WIDGET_DESKTOP_WIDTH = 1164;

/** Widget IDs that use wide (1048px) desktop width; all others use 600px. */
const WEB_WIDGET_DESKTOP_WIDTH_1024_IDS: ReadonlySet<WidgetTagKey> = new Set([
  'BILL_PROJECTION',  // Projected Bill
  'ENVIRONMENT',      // Carbon
  //'ENERGY_FLOW',      // Solar Energy Flow
  'RATE_PLAN_COMPARISON', //  Rate Plan Comparision
  'INSIGHTS_MONTHLY_SUMMARY', // Monthly Summary
  'INSIGHTS_SIMILAR_HOMES', // Similar Homes
  'INSIGHTS_ACTIVITY_MAP', // Activity Map
  'INSIGHTS_USAGE', // Usage Insights
  'RECOMMENDATION_TOP_TIPS', // Top Tips
  'RECOMMENDATION_TOP_PROGRAMS', // Top Programs
  'RECOMMENDATION_TIPS_LIBRARY', // Tips Library
  'SURVEY', // Survey
  'FAQ', // FAQ
  'USER_PREFERENCES', // User Preferences
  'GB_DOWNLOAD', // GB Download
  'FOOTER_MAIN', // Footer
  'HIGH_BILL_ANALYSER_V3', // High Bill Analyser
  'SMART_RECOMMENDATION', // Smart Recommendation
]);

/**
 * Returns the desktop preview width in px for a Web widget template.
 * Most widgets: 600px. Projected Bill, Carbon, Solar Flow: 1048px.
 * For multi-widget templates, returns WIDE width.
 */
export function getWebWidgetDesktopWidth(templateKey: string | null): number {
  if (!templateKey) return DEFAULT_WEB_WIDGET_DESKTOP_WIDTH;
  const widgetId = getWidgetIdFromTemplate(templateKey);
  if (!widgetId) return DEFAULT_WEB_WIDGET_DESKTOP_WIDTH;

  // Multi-widget templates use wide width
  if (Array.isArray(widgetId)) {
    return WIDE_WEB_WIDGET_DESKTOP_WIDTH;
  }

  return WEB_WIDGET_DESKTOP_WIDTH_1024_IDS.has(widgetId) ? WIDE_WEB_WIDGET_DESKTOP_WIDTH : DEFAULT_WEB_WIDGET_DESKTOP_WIDTH;
}

